import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";

// -------------------------
// Permissions tags par rôle / plan
// -------------------------
function getTagPermissions(profile) {
  if (!profile) {
    return {
      canUseAuto: false,
      canEditManual: false,
      isFamilyOffice: false,
    };
  }

  const role = profile.role || profile.user_role || "";
  const plan =
    profile.plan ||
    profile.subscription_plan ||
    profile.plan_type ||
    "standard";

  const isFamilyOffice =
    role === "family_office" || role === "familyOffice" || role === "FAMILY_OFFICE";

  if (isFamilyOffice) {
    // Colonne "Family Office" du tableau : vue seule
    return {
      canUseAuto: false,
      canEditManual: false,
      isFamilyOffice: true,
    };
  }

  const isAdvisor =
    role === "advisor" ||
    role === "art_advisor" ||
    role === "artAdvisor" ||
    role === "advisor_standard" ||
    role === "advisor_premium";

  const isPremium =
    ["premium", "user_premium", "premium_user", "advisor_premium"].includes(
      plan
    ) || plan?.toLowerCase?.().includes("premium");

  // Lecture du tableau :
  // - Standard user : Manuel
  // - Premium user : Auto + Manuel
  // - Art Advisor (Std + Prm) : Auto + Manuel
  if (isAdvisor) {
    return {
      canUseAuto: true,
      canEditManual: true,
      isFamilyOffice: false,
    };
  }

  if (isPremium) {
    return {
      canUseAuto: true,
      canEditManual: true,
      isFamilyOffice: false,
    };
  }

  // Standard "user" : uniquement manuel
  return {
    canUseAuto: false,
    canEditManual: true,
    isFamilyOffice: false,
  };
}

// -------------------------
// Composant principal
// -------------------------
const AdvancedTagsGenerator = ({ description, currentTags, setTags }) => {
  const { user, profile } = useAuth() || {};
  const { canEditManual, isFamilyOffice } =
    getTagPermissions(profile);

  const [selectedTags, setSelectedTags] = useState(currentTags || []);
  const [manualInput, setManualInput] = useState("");

  // On resynchronise si les tags externes changent
  useEffect(() => {
    setSelectedTags(currentTags || []);
  }, [currentTags]);

  const updateTags = (tags) => {
    const unique = Array.from(new Set(tags.filter(Boolean)));
    setSelectedTags(unique);
    if (typeof setTags === "function") {
      setTags(unique);
    }
  };

  const handleAddTag = (tag) => {
    const clean = (tag || "").trim();
    if (!clean) return;
    if (selectedTags.includes(clean)) return;
    updateTags([...selectedTags, clean]);
  };

  const handleRemoveTag = (tag) => {
    updateTags(selectedTags.filter((t) => t !== tag));
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!canEditManual) return;
    handleAddTag(manualInput);
    setManualInput("");
  };


  return (
    <section className="mt-8">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold">Tags &amp; catégorisation</h3>
        {user?.id && (
          <span className="text-xs text-gray-500">
            {selectedTags.length} tag(s) sélectionné(s)
          </span>
        )}
      </div>

      {isFamilyOffice && (
        <p className="mt-2 mb-4 text-xs text-gray-500">
          Vos clients gèrent eux-mêmes la catégorisation. Vous disposez d’une
          vue consolidée en lecture seule.
        </p>
      )}

      {/* --- Zone tags sélectionnés --- */}
      <div className="mb-4">
        {selectedTags.length === 0 ? (
          <p className="text-sm text-gray-500">
            Aucun tag n’est encore associé à cette œuvre.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800"
              >
                {tag}
                {canEditManual && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 text-gray-400 hover:text-red-500"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* --- Saisie manuelle (selon plan) --- */}
      {canEditManual && (
        <form
          onSubmit={handleManualSubmit}
          className="flex flex-wrap items-center gap-2 mb-4"
        >
          <input
            type="text"
            className="flex-1 min-w-[180px] border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="Ajouter un tag manuel (ex : abstraction, bleu, géométrique…) "
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
          />
          <button
            type="submit"
            className="px-3 py-1 text-sm rounded bg-black text-white hover:bg-gray-800 transition"
          >
            Ajouter
          </button>
        </form>
      )}

      {!canEditManual && !isFamilyOffice && (
        <p className="mb-4 text-xs text-gray-500">
          Votre plan actuel ne permet pas la modification manuelle des tags.
        </p>
      )}

    </section>
  );
};

export default AdvancedTagsGenerator;
